import { DirectApiError } from '@/lib/api/direct-client';
import type { OutlookSendBlockedReason, SendBlockedRecipient } from './types';

/** Backend error code for a send refused because the Outlook identity isn't connected. */
export const OUTLOOK_SEND_BLOCKED_CODE = 'OUTLOOK_SEND_BLOCKED';

export interface OutlookSendBlocked {
  reason: OutlookSendBlockedReason | undefined;
  blocked: SendBlockedRecipient[] | undefined;
  /**
   * The reason-specific sentence to show, for `reason` — the backend's own
   * copy, shared with the composer banner and the vale-ops portal (VALE-348).
   * Built without a display name, so `mailbox_not_connected` reads "The acting
   * solicitor's…"; a caller holding the resolved identity should prefer its
   * `blockedMessage`, which names the person.
   */
  reasonMessage: string | undefined;
}

/**
 * Narrow a thrown send error to an Outlook block, or `null` if it is anything
 * else (a validation 400, a network failure, an auth 401 — all of which must
 * keep propagating untouched).
 *
 * The backend refuses a blocked send with a **409 `OUTLOOK_SEND_BLOCKED`**
 * rather than a 2xx body carrying `status: 'blocked'`. That is deliberate: as
 * a 2xx, nine of the ten send surfaces ran their ordinary success path and
 * reported a refusal as a completed send (VALE-332). A 4xx makes honesty the
 * default for every caller, including ones written later — the cost being that
 * a caller wanting reason-specific copy has to narrow the error rather than
 * read a field. This helper is that narrowing, in one place, so no surface
 * re-derives it from a message string.
 */
export function getOutlookSendBlocked(
  error: unknown
): OutlookSendBlocked | null {
  if (!(error instanceof DirectApiError)) return null;
  if (error.response?.code !== OUTLOOK_SEND_BLOCKED_CODE) return null;

  return {
    reason: error.response.reason as OutlookSendBlockedReason | undefined,
    blocked: error.response.blocked as SendBlockedRecipient[] | undefined,
    reasonMessage: error.response.reasonMessage
  };
}
